import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import CompanySellerPicker from './CompanySellerPicker';
import { useOcrFile, useOcrSession } from './useOcrModal';
import { getImagePreview, MAX_NOTES_LENGTH } from './ocrActions';

const SourcePanel = ({ file, previewUrl, row, useExistingImage, busy, onToggleExisting, onSelectFile, onDrop, onExtract }) => {
  const fileInputRef = useRef(null);
  const imagePreview = getImagePreview(previewUrl, useExistingImage, row);
  return (
    <div className="col-md-5">
      <div className="fw-bold mb-2">1. Source image / document</div>
      <button
        type="button"
        className="unassigned-ocr-drop"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
      >
        <strong className="unassigned-ocr-drop-line">Paste a screenshot (Ctrl+V), drag &amp; drop, or click to browse</strong>
        <span className="unassigned-ocr-drop-line text-muted">JPG, PNG, HEIC or PDF, max 10 MB</span>
        {file && <span className="unassigned-ocr-drop-line">Selected: <strong>{file.name}</strong></span>}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        accept=".jpg,.jpeg,.png,.heic,.pdf,image/jpeg,image/png,image/heic,application/pdf"
        onChange={(e) => { onSelectFile(e.target.files?.[0]); e.target.value = ''; }}
      />
      <label className="d-flex align-items-center gap-2 mb-2">
        <input type="checkbox" checked={useExistingImage} onChange={(e) => onToggleExisting(e.target.checked)} />
        <span className="ms-2">Also use the existing package image</span>
      </label>
      <div className="unassigned-ocr-preview">
        {imagePreview ? <img src={imagePreview} alt="Package preview" /> : <span className="text-muted">No image preview</span>}
      </div>
      <Button variant="primary" className="w-100 mt-2" onClick={onExtract} disabled={Boolean(busy)}>
        {busy === 'extract' ? 'Extracting...' : 'Extract Text'}
      </Button>
    </div>
  );
};

SourcePanel.propTypes = {
  file: PropTypes.object,
  previewUrl: PropTypes.string,
  row: PropTypes.object.isRequired,
  useExistingImage: PropTypes.bool.isRequired,
  busy: PropTypes.string,
  onToggleExisting: PropTypes.func.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  onDrop: PropTypes.func.isRequired,
  onExtract: PropTypes.func.isRequired,
};

const MatchPanel = ({ match, isStale, seller, onSellerChange, companyOptions, sellerOptions }) => {
  if (!match) return null;
  if (!match.isMatch) {
    return (
      <div className="unassigned-ocr-nomatch">
        <h6>No matching seller found</h6>
        <p>Assign manually. The package stays in Unassigned Returns until it is assigned.</p>
        <CompanySellerPicker
          companyOptions={companyOptions}
          sellerOptions={sellerOptions}
          companyId={seller.companyId}
          sellerId={seller.sellerId}
          onChange={onSellerChange}
        />
      </div>
    );
  }
  return (
    <div className="alert alert-success mt-3 mb-0">
      <h6>Match found</h6>
      <div>Seller: <strong>{match.companyName} / {match.sellerName}</strong></div>
      <div>Basis: <strong>{match.basisLabel}{match.matchedValue ? ` (${match.matchedValue})` : ''}</strong></div>
      <div>Confidence: <strong>{match.confidence} ({match.confidenceScore}%)</strong></div>
      {!match.canAutoAssign && <div className="small mt-1">This match is not high confidence. Confirm it before assigning.</div>}
      {isStale && <div className="small mt-1 text-danger">Notes changed. Re-run the match to continue.</div>}
    </div>
  );
};

MatchPanel.propTypes = {
  match: PropTypes.object,
  isStale: PropTypes.bool,
  seller: PropTypes.object.isRequired,
  onSellerChange: PropTypes.func.isRequired,
  companyOptions: PropTypes.array.isRequired,
  sellerOptions: PropTypes.array.isRequired,
};

const NotesPanel = ({ row, session, companyOptions, sellerOptions }) => {
  const { notes, setNotes, busy, match, seller, setSeller, assignState } = session;
  const hasSavedNotes = row.notesPreview && row.notesPreview !== '-';
  return (
    <div className="col-md-7">
      <div className="fw-bold mb-2">2. Extracted notes (editable)</div>
      {hasSavedNotes && <div className="text-muted small mb-1">Saved notes: {row.notesPreview}. Saving replaces them.</div>}
      <textarea
        className="form-control unassigned-ocr-notes"
        value={notes}
        maxLength={MAX_NOTES_LENGTH}
        placeholder="Extracted text appears here. You can edit it before matching."
        onChange={(e) => setNotes(e.target.value)}
      />
      <div className="d-flex justify-content-end mt-2">
        <Button variant="outline-primary" size="sm" onClick={session.handleRematch} disabled={Boolean(busy) || !notes.trim()}>
          {busy === 'match' ? 'Matching...' : 'Re-run Match'}
        </Button>
      </div>
      <MatchPanel
        match={match}
        isStale={assignState.isStale}
        seller={seller}
        onSellerChange={setSeller}
        companyOptions={companyOptions}
        sellerOptions={sellerOptions}
      />
    </div>
  );
};

NotesPanel.propTypes = {
  row: PropTypes.object.isRequired,
  session: PropTypes.object.isRequired,
  companyOptions: PropTypes.array.isRequired,
  sellerOptions: PropTypes.array.isRequired,
};

const OcrModal = ({ show, row, companyOptions, sellerOptions, onHide, onAssigned }) => {
  const fileState = useOcrFile(show, row?.PackageId);
  const session = useOcrSession({ show, row, file: fileState.file, onHide, onAssigned });

  if (!row) return null;

  const { busy, notes, assignState } = session;

  return (
    <Modal show={show} onHide={session.handleClose} centered size="xl" className="modalDeleteWrapper">
      <Modal.Header closeButton>
        <Modal.Title>OCR - Extract Notes &amp; Auto-Assign</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-light border">
          <div className="row">
            <div className="col-md-6">Tracking: <strong>{row.trackingNumber}</strong></div>
            <div className="col-md-6">Carrier: <strong>{row.carrierName}</strong></div>
            <div className="col-md-6">Current: <strong>{row.companySellerName}</strong></div>
            <div className="col-md-6">Reason: <strong>{row.unassignedReason}</strong></div>
          </div>
        </div>
        <div className="row">
          <SourcePanel
            file={fileState.file}
            previewUrl={fileState.previewUrl}
            row={row}
            useExistingImage={session.useExistingImage}
            busy={busy}
            onToggleExisting={session.setUseExistingImage}
            onSelectFile={fileState.selectFile}
            onDrop={fileState.handleDrop}
            onExtract={session.handleExtract}
          />
          <NotesPanel row={row} session={session} companyOptions={companyOptions} sellerOptions={sellerOptions} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={session.handleClose} disabled={Boolean(busy)}>Cancel</Button>
        <Button variant="outline-primary" onClick={session.handleSaveNotes} disabled={Boolean(busy) || !notes.trim()}>
          {busy === 'save' ? 'Saving...' : 'Save Notes Only'}
        </Button>
        <Button variant="success" onClick={session.handleAssign} disabled={Boolean(busy) || !assignState.canAssign}>
          {busy === 'assign' ? 'Assigning...' : assignState.label}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

OcrModal.propTypes = {
  show: PropTypes.bool.isRequired,
  row: PropTypes.object,
  companyOptions: PropTypes.array.isRequired,
  sellerOptions: PropTypes.array.isRequired,
  onHide: PropTypes.func.isRequired,
  onAssigned: PropTypes.func.isRequired,
};

export default OcrModal;
